import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MtDriveVersion } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_DRIVE,
    StoreType,
    DriveFileProps,
    DriveVersionFilters,
    VersionList,

    LoadDriveVersions,
    SortDriveVersions,
    DeleteDriveVersions,
    UploadVersion,

    setDriveVersionList,
    setDriveVersionReloadList,
    setDriveVersionIds,
    setDriveVersionSort,
    setDriveVersionFilters,
    isRecycleOpened,
    isProjectFolderOpened,
    setDriveVersionSelected,
    closeModal,
} from "client/store"
import {
    Container,
    PageContent,
    Button,
    Loader,
    TableSearchWrapper,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import Search from "./search"
import Columns from "./columns"

const RevisionList = ({
    fileId,
}: DriveFileProps) => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)

    const file = useSelector((state: StoreType) => state.drive.file.fileEdit)
    const selected = useSelector((state: StoreType) => state.drive.version.selected)

    const versionList = useSelector((state: StoreType) => state.drive.version.versionList)
    const versionIds = useSelector((state: StoreType) => state.drive.version.versionIds)
    const sortColumn = useSelector((state: StoreType) => state.drive.version.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.drive.version.sortOrder)
    const filters = useSelector((state: StoreType) => state.drive.version.filters)
    const reloadList = useSelector((state: StoreType) => state.drive.version.reloadList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadDriveVersions(fileId).then((res) => {
            dispatch(setDriveVersionList(res))
            dispatch(setDriveVersionSelected([]))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortDriveVersions(Object.values(versionList), sortColumn, sortOrder, filters)
        dispatch(setDriveVersionIds(sortedIds))
    }, [versionList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtDriveVersion, order) => {
        dispatch(setDriveVersionSort(column, order))
    }

    const onFilter = (newFilters: DriveVersionFilters) => {
        dispatch(setDriveVersionFilters(newFilters))
    }

    const onDelete: DeleteRow = (version: MtDriveVersion, callback) => {
        DeleteDriveVersions([version], file.storage, userData.accountId).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const versionListCopy: VersionList = versionList ? deepCopy(versionList) : {}
                delete versionListCopy[version.id]
                dispatch(setDriveVersionList(versionListCopy))
                dispatch(setDriveVersionIds(versionIds.filter((id) => id !== version.id)))
            })
        })
    }

    const onAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files[0] || null
        if (!selectedFile) return

        setLoadState(PENDING)
        UploadVersion({
            file: selectedFile,
            storage: file.storage,
            userData,
            fileId,
            callbackSuccess: () => {
                dispatch(setDriveVersionReloadList(false))
                setTimeout(() => {
                    dispatch(setDriveVersionReloadList(true))
                }, 0)
            },
            callbackError: () => {
                setLoadState(IDLE)
            },
        })
    }

    const projectId = isProjectFolderOpened(drivePath)

    const accountEditAccess = hasAccess(PRM_DRIVE, EDIT)
    const projectEditAccess = hasProjectAccess(PRM_DRIVE, EDIT, projectId)
    const editAccess = !projectId
        ? accountEditAccess
        : projectEditAccess

    const recycleOpened = isRecycleOpened(drivePath)
    const allowedToEditFile = (
        (editAccess || file.owner === userData.userId)
        && file.accountId === userData.accountId
    )
    const allowedToAddVersion = allowedToEditFile && !recycleOpened

    const columns = Columns(allowedToEditFile)

    return (
        <Container>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <Search
                        id="driveVersionList"
                        rightOffset={allowedToAddVersion}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        allowedToAddVersion && (
                            <Button variant="success">
                                <input
                                    type="file"
                                    title={lang.selectFile}
                                    onChange={onAdd}
                                />
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={versionList}
                    dataIds={versionIds}
                    selectedIds={onSelectVersion ? selected : []}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                    bottomContent={onSelectVersion && (
                        <Button
                            variant="success"
                            disabled={!selected.length}
                            onClick={() => {
                                onSelectVersion(versionList[selected[0]], file)
                                dispatch(closeModal()) // close revision list modal
                                dispatch(closeModal()) // close drive list modal
                            }}
                        >
                            {lang.select}
                        </Button>
                    )}
                />
            </PageContent>
        </Container>
    )
}
export default RevisionList
